import { useMemo, useState, type FormEvent } from "react";
import { useUserProfile } from "../../auth/useUserProfile.ts";
import { OnboardingShell } from "../../components/onboarding/OnboardingShell.tsx";
import * as f from "../../components/onboarding/onboarding-form.styles.ts";
import { TOTAL_ONBOARDING_STEPS, getStepNumber } from "../../onboarding/steps.ts";
import { getBrowserClient } from "../../supabase/browser.ts";
import * as p from "./platform-selection.styles.ts";

// ─── Backend contract ────────────────────────────────────────────────────
//
// Mirrors the Phase 1A supported set enforced by public.select_platform
// (Migration 0021). The RPC normalises input via lower(trim(...)) and
// rejects anything not in this exact list. Adding a platform requires a
// Bible amendment + new migration that updates the server-side constant
// (and the users.preferred_platform CHECK constraint defence-in-depth).
type PlatformSlug = "betway_gh" | "sportybet" | "1xbet" | "betika";

interface PlatformOption {
  slug: PlatformSlug;
  title: string;
}

const PLATFORMS: readonly PlatformOption[] = [
  { slug: "betway_gh", title: "Betway Ghana" },
  { slug: "sportybet", title: "SportyBet" },
  { slug: "1xbet", title: "1xBet" },
  { slug: "betika", title: "Betika" },
] as const;

export function PlatformSelectionPage(): JSX.Element {
  const supabase = useMemo(() => getBrowserClient(), []);
  const { refetch } = useUserProfile();

  const [selectedSlug, setSelectedSlug] = useState<PlatformSlug>(PLATFORMS[0]!.slug);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    setBusy(true);
    const { error: rpcError } = await supabase.rpc("select_platform", {
      p_platform_slug: selectedSlug,
    });
    if (rpcError) {
      setError(rpcError.message);
      setBusy(false);
      return;
    }
    await refetch();
    // AuthGate sees a non-null preferred_platform on the next render and
    // computes the next unmet gate. With Step 13 in place, that gate is
    // /onboarding/phantom (no opt-out, no open Phantom session yet).
  }

  return (
    <OnboardingShell
      stepNumber={getStepNumber("platform")}
      totalSteps={TOTAL_ONBOARDING_STEPS}
      title="Choose your betting platform"
      description={
        "STRIKZA uses your selected platform to tailor slip presentation and prepare future " +
        "platform mapping. In Phase 1A this only prepares Phantom Mode — STRIKZA does not connect " +
        "to or log into your betting account, and never stores betting-platform credentials."
      }
    >
      <form style={f.formStack} onSubmit={handleSubmit} noValidate>
        {error ? <div style={f.message("error")}>{error}</div> : null}

        <div style={p.cardGroup} role="radiogroup" aria-label="Betting platform">
          {PLATFORMS.map((option) => {
            const isSel = selectedSlug === option.slug;
            return (
              <label key={option.slug} style={isSel ? p.cardSelected : p.card}>
                <input
                  type="radio"
                  name="platform"
                  value={option.slug}
                  checked={isSel}
                  onChange={() => setSelectedSlug(option.slug)}
                  style={p.cardInput}
                />
                <span style={p.cardBody}>
                  <span style={p.cardTitle}>{option.title}</span>
                  <span style={p.cardSlug}>{option.slug}</span>
                </span>
              </label>
            );
          })}
        </div>

        <div style={p.disclaimer}>
          STRIKZA never asks for your betting-platform username or password. Your selection here
          only sets a profile preference — it does not log into any bookmaker on your behalf.
        </div>

        <div style={f.buttonRow}>
          <button type="submit" style={f.primaryBtn} disabled={busy}>
            {busy ? "..." : "Save and continue"}
          </button>
        </div>
      </form>
    </OnboardingShell>
  );
}
